import type {
  CockpitRailSegment,
  CockpitRailState,
} from '../model/cockpitViewModel';
import { formatCockpitDateTime, formatDurationMs } from '../model/formatters';

export interface EvidenceRailSummary {
  windowMs: number;
  evidencedMs: number;
  withoutSignalMs: number;
  evidencedRatio: number;
  withoutSignalRatio: number;
}

export interface EvidenceRailTick {
  position: 'start' | 'middle' | 'end';
  instant: string;
  label: string;
}

export function summarizeEvidenceRail(
  segments: CockpitRailSegment[],
): EvidenceRailSummary {
  const windowMs = segments.reduce(
    (sum, segment) => sum + segment.durationMs,
    0,
  );
  const withoutSignalMs = segments.reduce(
    (sum, segment) =>
      segment.state === 'void' ? sum + segment.durationMs : sum,
    0,
  );
  const evidencedMs = Math.max(0, windowMs - withoutSignalMs);
  return {
    windowMs,
    evidencedMs,
    withoutSignalMs,
    evidencedRatio: windowMs === 0 ? 0 : evidencedMs / windowMs,
    withoutSignalRatio: windowMs === 0 ? 0 : withoutSignalMs / windowMs,
  };
}

export function evidenceRailFlex(segment: CockpitRailSegment): string {
  return `${segment.durationMs} 1 0%`;
}

export function createEvidenceRailTicks(
  segments: CockpitRailSegment[],
): EvidenceRailTick[] {
  if (segments.length === 0) return [];
  const start = segments[0].startedAt;
  const end = segments[segments.length - 1].endedAt;
  const startMs = Date.parse(start);
  const endMs = Date.parse(end);
  const middle =
    Number.isFinite(startMs) && Number.isFinite(endMs)
      ? new Date(startMs + (endMs - startMs) / 2).toISOString()
      : start;
  return [
    { position: 'start', instant: start, label: formatCockpitDateTime(start) },
    {
      position: 'middle',
      instant: middle,
      label: formatCockpitDateTime(middle),
    },
    { position: 'end', instant: end, label: formatCockpitDateTime(end) },
  ];
}

export function observedRailStates(
  segments: CockpitRailSegment[],
): CockpitRailState[] {
  return segments.reduce<CockpitRailState[]>((states, segment) => {
    if (!states.includes(segment.state)) states.push(segment.state);
    return states;
  }, []);
}

export function railStateLabel(state: CockpitRailState): string {
  const labels: Record<CockpitRailState, string> = {
    evidence: 'Evidência confirmada',
    running: 'Em execução',
    detected: 'Detectado',
    inferred: 'Inferido',
    covered: 'Sessão observada',
    wait: 'Espera observada',
    void: 'Sem sinal registrado',
  };
  return labels[state];
}

export function segmentAccessibleName(segment: CockpitRailSegment): string {
  const identification =
    segment.state === 'void' ? 'intervalo sem sinal' : `sinal ${segment.label}`;
  const action = segment.nodeId ? '; selecionar evidência' : '';
  return `${railStateLabel(
    segment.state,
  )}; ${identification}; de ${formatCockpitDateTime(
    segment.startedAt,
  )} até ${formatCockpitDateTime(segment.endedAt)}; duração ${formatDurationMs(
    segment.durationMs,
  )}${action}`;
}

export function formatRatio(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'percent',
    maximumFractionDigits: 1,
  }).format(value);
}
